import { CheckOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { type SelectProps, ConfigProvider, Divider } from 'antd5';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import { Modal } from 'components/ui';
import { useVendorOrgRelationshipOptions } from 'utils/organization';

import { AddVendorContainer, VendorSelect } from './VendorOrgChooser.style';

interface Props extends SelectProps {}

export function VendorOrgChooser(props: Props) {
  const { t } = useTranslation();
  const { loading, vendorOptions } = useVendorOrgRelationshipOptions();
  const [open, _setOpen] = useState<boolean>(false);

  // small wait to let the antd dropdown animations get outta the way
  const setOpen = useDebouncedCallback((value: boolean) => _setOpen(value), 100);

  return (
    <ConfigProvider renderEmpty={() => <RenderEmpty />}>
      <Modal
        data-testid="add-vendor-modal"
        footer={null}
        onCancel={() => setOpen(false)}
        open={open}
        title={t('components.addVendor', 'Add Vendor')}
      >
        <Trans
          i18nKey="components.vendorSupportRequest"
          t={t}
          defaults="Please contact <supportEmail>support@skyspecs.com</supportEmail> to add an available vendor to your account. Once that relationship has been established, you will be able to attach that vendor to your work orders."
          components={{
            // content is injected by Trans at runtime
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            supportEmail: <a href="mailto:support@skyspecs.com" />,
          }}
        />
      </Modal>

      <VendorSelect
        data-testid="add-vendor-select"
        allowClear={true}
        defaultActiveFirstOption={false}
        dropdownRender={content => (
          <RenderDropdown content={content} setOpen={open => setOpen(open)} />
        )}
        loading={loading}
        menuItemSelectedIcon={<CheckOutlined />}
        options={vendorOptions}
        placeholder={t('components.setVendor', 'Set Vendor')}
        popupMatchSelectWidth={false}
        {...props}
      />
    </ConfigProvider>
  );
}

interface RenderDropdownProps {
  content: React.ReactNode;
  setOpen: (open: boolean) => void;
}

function RenderDropdown({ content, setOpen }: RenderDropdownProps) {
  const { t } = useTranslation();
  return (
    <div id="vendor-chooser-dropdown">
      <div
        data-testid="add-vendor"
        // have to use onMouseDown rather than onClick cuz antd
        onMouseDown={() => setOpen(true)}
      >
        <AddVendorContainer>
          <PlusOutlined style={{ marginRight: '4px' }} />
          <span>{t('components.addVendor', 'Add Vendor')}</span>
        </AddVendorContainer>
      </div>

      <Divider style={{ margin: '4px 0' }} />

      {content}
    </div>
  );
}

function RenderEmpty() {
  const { t } = useTranslation();
  return (
    <div>
      <UserAddOutlined style={{ marginRight: '4px' }} />
      <span>{t('components.noVendors', 'No vendors')}</span>
    </div>
  );
}
